import React, { useCallback, useEffect } from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import Modal from 'components/Modal';
import {
    Box, Button, Text, Image, Link, Flex, Icon
} from 'components/ui';
import mediaUtils from 'utils/Media';
import { space } from 'style/config';
import processEvent from 'analytics/processEvent';
import anaConsts from 'analytics/constants';
import passkeysUtils from 'utils/passkeys';

const { Media } = mediaUtils;

const PasskeyOptinModal = props => {
    const {
        isOpen, onDismiss, onSetupPasskey, passkeyType, localization = {}, displayInfoModal, onSuccess, placement
    } = props;

    useEffect(() => {
        if (isOpen) {
            switch (passkeyType) {
                case 'optinCreateAccount':
                    processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
                        data: {
                            pageName: anaConsts.PAGE_NAMES.ACC_SETTING_PASSKEY_CREATE
                        }
                    });

                    break;
                case 'optinCreate': {
                    // ACS-3078 AC1: Get current skip count from localStorage
                    const trackingData = passkeysUtils.getTrackingData();
                    const skipCount = trackingData?.count || 0;

                    // Only include event284 if count is 0, 1, or 2 (as per AC1)
                    const eventData = {
                        pageName: anaConsts.PAGE_NAMES.PASSKEY_OPTIN_LOAD,
                        linkData: placement
                    };

                    // AC1: Pass event284=[number of times user has skipped]
                    // Possible values: 0, 1, or 2
                    if (skipCount >= 0 && skipCount <= 2) {
                        eventData.eventStrings = [anaConsts.Event.EVENT_284 + '=' + skipCount];
                    }

                    processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
                        data: eventData
                    });

                    break;
                }
                default:
                    break;
            }
        }
    }, [isOpen, passkeyType, placement]);

    const handleClose = useCallback(() => {
        const maybeLater = passkeyType === 'optinCreate';

        if (onDismiss) {
            onDismiss(maybeLater);
        }
    }, [onDismiss, passkeyType]);

    const handleSuccess = useCallback(() => {
        if (onSuccess) {
            onSuccess({
                title: '',
                message: (
                    <Flex
                        flexDirection='column'
                        alignItems='center'
                        justifyContent='center'
                        gap={space[6]}
                        paddingTop={space[5]}
                        paddingBottom={space[7]}
                    >
                        <Image
                            src='/img/ufe/success-passkey.svg'
                            alt='Success'
                            disableLazyLoad={true}
                        />
                        <Text fontSize='sm'>{localization.passkeyCreated}</Text>
                    </Flex>
                ),
                showFooter: false,
                showCloseButton: false,
                width: 270
            });
            processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
                data: {
                    pageName: anaConsts.PAGE_NAMES.PASSKEY_OPTIN_CREATED,
                    eventStrings: ['event285']
                }
            });
        }
    }, [onSuccess]);

    const handleSetupPasskey = useCallback(async () => {
        if (onSetupPasskey) {
            try {
                processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
                    data: {
                        actionInfo: anaConsts.LinkData.PASSKEY_CONTINUE
                    }
                });

                const didSetup = await onSetupPasskey();

                if (didSetup) {
                    handleSuccess();
                }
            } catch (error) {
                Sephora.logger?.error('Passkey setup failed', error);
                processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
                    data: {
                        eventStrings: ['event286']
                    }
                });
            }
        }
    }, [onSetupPasskey, handleSuccess]);

    const heading = () => {
        if (passkeyType === 'optinCreate') {
            return localization.optinCreateHeading;
        } else if (passkeyType === 'optinCreateAccount') {
            return localization.optinCreateAccountHeading;
        }

        return '';
    };

    const showInfoModal = () => {
        displayInfoModal({
            isOpen: true
        });
    };

    const renderButton = () => {
        switch (passkeyType) {
            case 'optinCreate':
                return (
                    <>
                        <Button
                            variant='secondary'
                            onClick={handleClose}
                            style={{ flex: 1 }}
                            dataAt={Sephora.debug.dataAt('maybe_later_button')}
                        >
                            {localization.maybeLaterButton}
                        </Button>
                        <Button
                            variant='primary'
                            onClick={handleSetupPasskey}
                            style={{ flex: 1 }}
                            dataAt={Sephora.debug.dataAt('setup_passkey_button')}
                        >
                            {localization.setupButton}
                        </Button>
                    </>
                );
            case 'optinCreateAccount':
                return (
                    <>
                        <Button
                            variant='secondary'
                            onClick={handleClose}
                            style={{ flex: 1 }}
                            dataAt={Sephora.debug.dataAt('cancel_button')}
                        >
                            {localization.cancel}
                        </Button>
                        <Button
                            variant='primary'
                            onClick={handleSetupPasskey}
                            dataAt={Sephora.debug.dataAt('create_new_passkey_button')}
                        >
                            {localization.createNewPasskey}
                        </Button>
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onDismiss={handleClose}
            width={0}
            dataAt={Sephora.debug.dataAt('passkey_optin_modal')}
        >
            <Modal.Header hasBorder={false}></Modal.Header>

            <Modal.Body
                lineHeight='tight'
                paddingTop={null}
            >
                <Box marginBottom={4}>
                    <Media lessThan='md'>
                        <Image
                            display='block'
                            size={96}
                            marginBottom={4}
                            margin='0'
                            src='/img/ufe/passkey-modal-image.svg'
                        />
                    </Media>
                    <Media greaterThanOrEqual='md'>
                        <Image
                            display='block'
                            size={64}
                            marginBottom={4}
                            marginX='0'
                            src='/img/ufe/passkey-modal-image-desktop.svg'
                        />
                    </Media>

                    <Text
                        is='h2'
                        fontWeight='bold'
                        fontSize='md'
                        marginBottom={4}
                    >
                        {heading()}
                    </Text>

                    <Text
                        is='p'
                        marginBottom={4}
                    >
                        {localization.subheading}
                        <Icon
                            name='infoOutline'
                            size={16}
                            marginLeft={1}
                            color='gray'
                            css={{ cursor: 'pointer' }}
                            onClick={showInfoModal}
                        />
                    </Text>

                    <Text
                        is='p'
                        marginBottom={4}
                        lineHeight='tight'
                        fontSize='sm'
                        color='gray'
                    >
                        {localization.warningNotification}
                    </Text>

                    <Text
                        is='p'
                        lineHeight='tight'
                        fontSize='sm'
                        color='gray'
                        my={4}
                        {...props}
                    >
                        {localization.termsAndConditions}
                        <Link
                            color='blue'
                            underline={true}
                            href='/terms-of-use'
                            fontWeight='bold'
                        >
                            {localization.termsOfUse}
                        </Link>
                        {localization.termsAndConditionsRest}
                        <Link
                            color='blue'
                            underline={true}
                            href='/privacy-policy'
                        >
                            {localization.privacyPolicy}
                        </Link>
                    </Text>
                </Box>
                <Flex
                    gap={4}
                    flexDirection={['column-reverse', 'row']}
                >
                    {renderButton()}
                </Flex>
            </Modal.Body>
        </Modal>
    );
};

export default wrapFunctionalComponent(PasskeyOptinModal, 'PasskeyOptinModal');
